// useFindSimilar.ts
import type { components } from '@feedfm/clips-services-api';
import { useCallback, useRef, useState } from 'react';
import { useStudioAuthenticatedContext } from '../../../context/StudioContext';
import { useToast } from '../../../context/ToastContext';
import { hydrateSimilarResults } from './findSimilarModel';

type TrackObject = components['schemas']['TrackObject'];
type SimilarTrack = components['schemas']['SimilarTrack'];

// The /similar-tracks endpoint caps `limit` at 10 (matches SimilarTracksModal).
const SIMILAR_FETCH_LIMIT = 10;
// We can't render more results than the endpoint returns; cap bounds the
// per-result catalog hydration lookups.
const SIMILAR_RESULT_CAP = 10;

export interface FindSimilarSeed {
  trackId: number;
  title: string;
  artist: string;
}

export interface UseFindSimilarReturn {
  seed: FindSimilarSeed | null;
  results: TrackObject[];
  loading: boolean;
  error: string | null;
  findSimilar: (track: TrackObject) => void;
  clear: () => void;
}

export const useFindSimilar = (): UseFindSimilarReturn => {
  const { backendClient } = useStudioAuthenticatedContext();
  const { showToast } = useToast();

  const [seed, setSeed] = useState<FindSimilarSeed | null>(null);
  const [results, setResults] = useState<TrackObject[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchingRef = useRef(false);

  const clear = useCallback(() => {
    setSeed(null);
    setResults([]);
    setError(null);
  }, []);

  const findSimilar = useCallback(
    (track: TrackObject) => {
      if (fetchingRef.current) return;
      fetchingRef.current = true;
      setLoading(true);
      setError(null);
      setResults([]);
      setSeed({
        trackId: track.id,
        title: track.title,
        artist: track.display_artist_name,
      });

      void (async () => {
        try {
          const response = await backendClient.similarTracks({
            audioUrl: track.track_preview?.url ?? undefined,
            title: track.title,
            displayArtistName: track.display_artist_name,
            limit: SIMILAR_FETCH_LIMIT,
            includeVibeTags: true,
          });

          const similar: SimilarTrack[] = (response.similarTracks ?? []).slice(
            0,
            SIMILAR_RESULT_CAP,
          );

          // Hydrate each similarity result against the catalog by title+artist.
          const candidatesByIndex = await Promise.all(
            similar.map(async (s) => {
              try {
                // Substring filter (forgiving of casing/punctuation) + a generous candidate limit so the exact match isn't truncated out; pickCatalogMatch does the precise normalized match.
                const res = await backendClient.getCurationCatalog({
                  pagination: { limit: 25, offset: 0 },
                  filters: {
                    titleContains: s.title,
                    artistNameContains: s.displayArtistName,
                  },
                  sortOrder: 'asc',
                });
                return res.tracks ?? [];
              } catch {
                return [];
              }
            }),
          );

          setResults(
            hydrateSimilarResults({
              results: similar,
              candidatesByIndex,
              seedTrackId: track.id,
            }),
          );
        } catch (err) {
          setError(
            err instanceof Error ? err.message : 'Failed to find similar tracks',
          );
          showToast('Failed to find similar tracks', 'error');
          setSeed(null);
        } finally {
          setLoading(false);
          fetchingRef.current = false;
        }
      })();
    },
    [backendClient, showToast],
  );

  return { seed, results, loading, error, findSimilar, clear };
};
